import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  MapPin,
  Package,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Wifi,
  WifiOff,
  Terminal,
} from 'lucide-react';
import { useSocketContext } from '../../App';

const navItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/zones', label: 'Zones', icon: MapPin },
  { path: '/items', label: 'Items', icon: Package },
  { path: '/analytics', label: 'Analytics', icon: BarChart3 },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const socket = useSocketContext();
  const location = useLocation();

  return (
    <aside
      className={`flex flex-col h-screen bg-terminal-surface border-r border-terminal-border transition-all duration-300 ${
        collapsed ? 'w-16' : 'w-56'
      }`}
    >
      {/* Logo */}
      <div className="flex items-center gap-2 px-4 h-14 border-b border-terminal-border shrink-0">
        <Terminal className="w-5 h-5 text-accent-green shrink-0" />
        {!collapsed && (
          <div className="animate-fade-in overflow-hidden">
            <h1 className="text-sm font-bold text-accent-green tracking-wider leading-none">
              DayZ ECONOMY
            </h1>
            <span className="text-[10px] text-text-muted font-mono">TERMINAL v1.0</span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-2 space-y-1">
        {navItems.map(({ path, label, icon: Icon }) => {
          const isActive =
            path === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(path);

          return (
            <NavLink
              key={path}
              to={path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-150 group ${
                isActive
                  ? 'bg-accent-green/10 text-accent-green border border-accent-green/20'
                  : 'text-text-secondary hover:text-text-primary hover:bg-terminal-border/50 border border-transparent'
              }`}
            >
              <Icon
                className={`w-4 h-4 shrink-0 ${
                  isActive ? 'text-accent-green' : 'text-text-muted group-hover:text-text-secondary'
                }`}
              />
              {!collapsed && (
                <span className="animate-fade-in truncate">{label}</span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Connection Status */}
      <div className="px-3 py-3 border-t border-terminal-border">
        <div className="flex items-center gap-2">
          {socket?.isConnected ? (
            <>
              <div className="w-2 h-2 rounded-full bg-accent-green animate-pulse-green shrink-0" />
              {!collapsed && (
                <div className="flex items-center gap-1 animate-fade-in">
                  <Wifi className="w-3 h-3 text-accent-green" />
                  <span className="text-[11px] text-accent-green font-mono">CONNECTED</span>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="w-2 h-2 rounded-full bg-accent-red shrink-0" />
              {!collapsed && (
                <div className="flex items-center gap-1 animate-fade-in">
                  <WifiOff className="w-3 h-3 text-accent-red" />
                  <span className="text-[11px] text-accent-red font-mono">OFFLINE</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Collapse Toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center justify-center h-10 border-t border-terminal-border text-text-muted hover:text-text-primary hover:bg-terminal-border/50 transition-colors"
      >
        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>
    </aside>
  );
}
